// react
import { type ReactNode, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Popover,
  TextField,
} from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';

// relative
import { ConfirmButton } from '../../ui/button/ConfirmButton';
import { type DateRangePreset, getDefaultPresets } from './dateRangePresets';

import { type DateTime, DateTime as LuxonDateTime } from 'luxon';

type PopoverChildren =
  ReactNode | ((helpers: { close: () => void }) => ReactNode);

interface AzureStyleDateRangeSelectorProps {
  /** Defaults to `getDefaultPresets(t)`. */
  presets?: DateRangePreset[];
  /** Show and edit hours and minutes. Defaults to true. */
  includeTime?: boolean;
  /** Prevent picking dates after today. */
  disableFuture?: boolean;
  /** Show a warning icon in the range field. */
  showWarning?: boolean;
  /** Extra content rendered at the bottom of the popover. */
  children?: PopoverChildren;
  from: DateTime | null;
  to: DateTime | null;
  onRangeChange: (range: { from: DateTime; to: DateTime }) => void;
  rangeButtonDataTour?: string;
}

interface RangeDialogContentProps {
  from: DateTime | null;
  to: DateTime | null;
  presets: DateRangePreset[];
  includeTime: boolean;
  disableFuture: boolean;
  children?: ReactNode;
  onCancel: () => void;
  onConfirm: (range: { from: DateTime; to: DateTime }) => void;
}

const RangeDialogContent = ({
  from,
  to,
  presets,
  includeTime,
  disableFuture,
  children,
  onCancel,
  onConfirm,
}: RangeDialogContentProps) => {
  const { t } = useTranslation('view-data');
  // Include 'month' so picking a year leads to the month selector.
  const renderPicker = (
    label: string,
    value: DateTime | null,
    onChange: (value: DateTime | null) => void,
    textField: Record<string, unknown>,
  ) =>
    includeTime ? (
      <DateTimePicker
        label={label}
        value={value}
        onChange={onChange}
        views={['year', 'month', 'day', 'hours', 'minutes']}
        disableFuture={disableFuture}
        slotProps={{ textField }}
      />
    ) : (
      <DatePicker
        label={label}
        value={value}
        onChange={onChange}
        views={['year', 'month', 'day']}
        disableFuture={disableFuture}
        slotProps={{ textField }}
      />
    );
  const [draftFrom, setDraftFrom] = useState<DateTime | null>(from);
  const [draftTo, setDraftTo] = useState<DateTime | null>(to);

  const isValid =
    !!draftFrom &&
    !!draftTo &&
    draftFrom.isValid &&
    draftTo.isValid &&
    draftFrom <= draftTo;
  const endBeforeStart =
    !!draftFrom &&
    !!draftTo &&
    draftFrom.isValid &&
    draftTo.isValid &&
    draftTo < draftFrom;

  return (
    <Box sx={{ p: 2, width: 340, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
        }}
      >
        {presets.map((preset) => (
          <Button
            key={preset.key}
            variant="outlined"
            size="small"
            onClick={() => onConfirm(preset.getRange())}
          >
            {preset.label}
          </Button>
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {renderPicker(t('start'), draftFrom, setDraftFrom, { size: 'small' })}
        {renderPicker(t('end'), draftTo, setDraftTo, {
          size: 'small',
          error: endBeforeStart,
          helperText: endBeforeStart ? t('end_before_start') : undefined,
        })}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button size="small" color="inherit" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <ConfirmButton
          size="small"
          variant="contained"
          disabled={!isValid}
          onClick={() => {
            if (draftFrom && draftTo) {
              onConfirm(
                includeTime
                  ? { from: draftFrom, to: draftTo }
                  : {
                      from: draftFrom.startOf('day'),
                      to: draftTo.endOf('day'),
                    },
              );
            }
          }}
        >
          {t('confirm')}
        </ConfirmButton>
      </Box>

      {children}
    </Box>
  );
};

const AzureStyleDateRangeSelector = ({
  presets,
  includeTime = true,
  disableFuture = false,
  showWarning = false,
  children,
  from,
  to,
  onRangeChange,
  rangeButtonDataTour,
}: AzureStyleDateRangeSelectorProps) => {
  const { t } = useTranslation('view-data');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const closePopover = () => setAnchorEl(null);

  const formatDate = (dt: DateTime | null) =>
    dt?.isValid
      ? dt.toLocaleString(
          includeTime ? LuxonDateTime.DATETIME_SHORT : LuxonDateTime.DATE_SHORT,
        )
      : '-';

  const rangeText = `${formatDate(from)} - ${formatDate(to)}`;

  return (
    <>
      <TextField
        size="small"
        variant="outlined"
        value={rangeText}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-tour={rangeButtonDataTour}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {showWarning && (
                  <WarningRoundedIcon
                    fontSize="small"
                    color="warning"
                    sx={{ mr: 0.5 }}
                  />
                )}
                <CalendarTodayRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
            sx: { cursor: 'pointer' },
          },
          htmlInput: {
            // `ch` resolves in the input's own font, so the field hugs the text.
            sx: {
              cursor: 'pointer',
              width: {
                xs: '100%',
                md: `${includeTime ? rangeText.length - 7 : rangeText.length - 3}ch`,
              },
            },
          },
        }}
        sx={{ width: { xs: '100%', md: 'auto' } }}
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <RangeDialogContent
          from={from}
          to={to}
          presets={presets ?? getDefaultPresets(t)}
          includeTime={includeTime}
          disableFuture={disableFuture}
          onCancel={closePopover}
          onConfirm={(range) => {
            onRangeChange(range);
            closePopover();
          }}
        >
          {typeof children === 'function'
            ? children({ close: closePopover })
            : children}
        </RangeDialogContent>
      </Popover>
    </>
  );
};

export default AzureStyleDateRangeSelector;
